import React, { useRef, useState, useCallback } from "react";

const PULL_THRESHOLD = 72;
const MAX_PULL = 120;

/** Touch pull-to-refresh wrapper — use at scroll top of a section or page. */
export default function PullToRefresh({
  onRefresh,
  refreshing = false,
  children,
  testId = "pull-to-refresh",
  releaseLabel = "Release to refresh",
  pullLabel = "Pull to refresh",
}) {
  const containerRef = useRef(null);
  const startYRef = useRef(0);
  const pullingRef = useRef(false);
  const [pullDistance, setPullDistance] = useState(0);

  const canStartPull = useCallback(() => {
    const el = containerRef.current;
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return rect.top >= -8;
  }, []);

  const onTouchStart = useCallback((e) => {
    if (refreshing || !canStartPull()) return;
    startYRef.current = e.touches[0].clientY;
    pullingRef.current = true;
  }, [refreshing, canStartPull]);

  const onTouchMove = useCallback((e) => {
    if (!pullingRef.current || refreshing) return;
    const dy = e.touches[0].clientY - startYRef.current;
    if (dy <= 0) {
      setPullDistance(0);
      return;
    }
    if (!canStartPull()) {
      pullingRef.current = false;
      setPullDistance(0);
      return;
    }
    e.preventDefault();
    setPullDistance(Math.min(dy, MAX_PULL));
  }, [refreshing, canStartPull]);

  const onTouchEnd = useCallback(() => {
    if (!pullingRef.current) return;
    pullingRef.current = false;
    const shouldRefresh = pullDistance >= PULL_THRESHOLD;
    setPullDistance(0);
    if (shouldRefresh && onRefresh) onRefresh();
  }, [pullDistance, onRefresh]);

  return (
    <div
      ref={containerRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      className="relative"
      data-testid={testId}
    >
      {(pullDistance > 0 || refreshing) && (
        <div
          className="flex justify-center items-center text-ink/60 font-ui text-[13px] font-medium overflow-hidden"
          style={{ height: pullDistance > 0 ? pullDistance : 40 }}
          role="status"
          aria-live="polite"
        >
          {refreshing
            ? "Refreshing…"
            : pullDistance >= PULL_THRESHOLD
              ? releaseLabel
              : pullLabel}
        </div>
      )}
      {children}
    </div>
  );
}
